import { useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { ClientDropdown } from '@/components/ui/client-dropdown'
import { FilterableSelect } from '@/components/ui/filterable-select'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useBankAccounts } from './hooks/useReconApi'
import type { DetailedFile } from './hooks/useReconApi'
import { Upload, Loader2, Settings2 } from 'lucide-react'
import { Link } from 'react-router-dom'

interface ContextStripProps {
  clientId: string | null
  onClientChange: (id: string | null) => void
  files: DetailedFile[]
  filesLoading: boolean
  onUploadClick: () => void
  accountId: number | null
  onAccountChange: (id: number | null) => void
  statementId: string | null
  onStatementChange: (id: string | null) => void
  unmatchedTotal: number
  currency: string
}

export function ContextStrip({
  clientId,
  onClientChange,
  files,
  filesLoading,
  onUploadClick,
  accountId,
  onAccountChange,
  statementId,
  onStatementChange,
  unmatchedTotal,
  currency,
}: ContextStripProps) {
  const { data: bankAccounts, isLoading: loadingAccounts } = useBankAccounts(clientId)

  const hasAccount = accountId != null

  const bankAccountOptions = useMemo(
    () =>
      (bankAccounts ?? []).map((acc) => {
        const code = `${acc.code}${acc.sub_code ? `.${acc.sub_code}` : ''}`
        return {
          value: acc.id.toString(),
          label: acc.name,
          details: code,
          searchText: code,
        }
      }),
    [bankAccounts]
  )

  const renderBankAccountOption = (opt: { details?: string; label: string }) => (
    <span className="flex items-center gap-2 min-w-0">
      {opt.details && (
        <span className="text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded shrink-0 font-mono">
          {opt.details}
        </span>
      )}
      <span className="truncate">{opt.label}</span>
    </span>
  )

  const statementOptions = useMemo(
    () =>
      files.map((file) => ({
        value: file.id,
        label: file.original_filename,
        details: `${file.transaction_count} txns`,
        searchText: file.original_filename,
        status: file.status,
      })),
    [files]
  )

  const renderStatementOption = (opt: {
    details?: string
    label: string
    status?: string
  }) => (
    <span className="flex items-center gap-2 min-w-0">
      {opt.status === 'processing' ? (
        <Loader2 className="h-2.5 w-2.5 animate-spin text-blue-500 shrink-0" />
      ) : (
        <span
          className={`inline-block h-2 w-2 shrink-0 rounded-full ${
            opt.status === 'completed'
              ? 'bg-green-500'
              : opt.status === 'failed'
                ? 'bg-red-500'
                : 'bg-neutral-400'
          }`}
        />
      )}
      <span className="truncate">{opt.label}</span>
      {opt.details && (
        <span className="ml-auto text-xs bg-neutral-200 text-neutral-700 px-1.5 py-0.5 rounded shrink-0 tabular-nums">
          {opt.details}
        </span>
      )}
    </span>
  )

  return (
    <div className="flex items-center gap-4 rounded-xl bg-neutral-100 px-6 py-4">
      <div className="flex items-center gap-3">
        <ClientDropdown
          value={clientId ?? ''}
          onValueChange={(val) => onClientChange(val || null)}
          placeholder="Select client..."
          multiple={false}
          className="h-9 w-auto shrink-0"
        />

        <FilterableSelect
          value={accountId?.toString() ?? ''}
          onValueChange={(val) => onAccountChange(val && val !== 'none' ? Number(val) : null)}
          options={bankAccountOptions}
          disabled={!clientId}
          loading={loadingAccounts}
          placeholder="Select bank account"
          searchPlaceholder="Search by code or bank..."
          emptyMessage="No matching accounts"
          triggerClassName="h-9 w-[280px] shrink-0"
          renderOption={renderBankAccountOption}
          renderSelectedValue={renderBankAccountOption}
        />

        {hasAccount && (
          <FilterableSelect
            value={statementId ?? ''}
            onValueChange={(val) => onStatementChange(val && val !== 'none' ? val : null)}
            options={statementOptions}
            loading={filesLoading}
            disabled={files.length === 0}
            placeholder={files.length === 0 ? 'No statements uploaded' : 'Select bank statement'}
            searchPlaceholder="Search statements..."
            emptyMessage="No matching statements"
            triggerClassName="h-9 w-[280px] shrink-0"
            renderOption={renderStatementOption}
            renderSelectedValue={renderStatementOption}
          />
        )}
      </div>

      <div className="ml-auto flex items-center gap-3">
        {hasAccount && statementId && (
          <div className="shrink-0 text-right">
            <p className="text-[11px] uppercase text-neutral-500">Unmatched</p>
            <p className={`text-sm font-semibold tabular-nums ${unmatchedTotal > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {unmatchedTotal.toLocaleString('bg-BG', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{' '}
              {currency}
            </p>
          </div>
        )}

        {(() => {
          const disabled = !clientId || !hasAccount
          const hint = !clientId
            ? 'Select a client first'
            : !hasAccount
              ? 'Select a bank account first'
              : null
          const button = (
            <Button
              variant="outline"
              onClick={onUploadClick}
              disabled={disabled}
              className="h-9 shrink-0"
            >
              <Upload className="mr-1.5 h-3.5 w-3.5" />
              Upload
            </Button>
          )
          if (!hint) return button
          return (
            <TooltipProvider delayDuration={50}>
              <Tooltip>
                {/* Wrap disabled button so the tooltip still fires on hover */}
                <TooltipTrigger asChild>
                  <span tabIndex={0} className="shrink-0">{button}</span>
                </TooltipTrigger>
                <TooltipContent className="duration-[100ms]">{hint}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )
        })()}

        <Link to={clientId ? `/reconciliation/files?client=${clientId}` : '/reconciliation/files'}>
          <Button variant="ghost" className="h-9 shrink-0 text-neutral-500">
            <Settings2 className="mr-1.5 h-3.5 w-3.5" />
            Manage files
          </Button>
        </Link>
      </div>
    </div>
  )
}
